import React from 'react';
import styled from 'styled-components';
import { Lock, MessageCircle, ArrowRight } from 'lucide-react';
import { theme } from '../styles/theme';

const Container = styled.div`
  max-width: 500px;
  width: 100%;
  margin: 0 auto;
  padding: 32px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.1) 100%);
  backdrop-filter: blur(24px);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(99, 102, 241, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.2);
  font-family: ${theme.typography.fontFamily};
  text-align: center;
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  margin: 0 auto 24px;
  background: rgba(239, 68, 68, 0.2);
  border: 2px solid rgba(239, 68, 68, 0.3);
  border-radius: 50%;
  backdrop-filter: blur(10px);
`;

const Title = styled.h2`
  color: white;
  font-size: ${theme.typography.sizes['2xl']};
  font-weight: ${theme.typography.weights.bold};
  margin: 0 0 16px 0;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
`;

const Message = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: ${theme.typography.sizes.lg};
  line-height: 1.6;
  margin: 0 0 32px 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
`;

const NextButton = styled.button`
  width: 100%;
  padding: 16px 24px;
  background: rgba(99, 102, 241, 0.9);
  backdrop-filter: blur(20px);
  color: white;
  border: 1px solid rgba(99, 102, 241, 0.4);
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.weights.semibold};
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  font-family: ${theme.typography.fontFamily};
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  
  &:hover {
    background: rgba(79, 70, 229, 0.9);
    border-color: rgba(79, 70, 229, 0.6);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(99, 102, 241, 0.3);
  }
`;

const SuggestionBox = styled.div`
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.3);
  border-radius: 12px;
  padding: 20px;
  margin: 24px 0;
  backdrop-filter: blur(10px);
`;

const SuggestionTitle = styled.h3`
  color: #4ade80;
  font-size: ${theme.typography.sizes.base};
  font-weight: ${theme.typography.weights.semibold};
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
`;

const SuggestionText = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: ${theme.typography.sizes.sm};
  margin: 0;
  line-height: 1.5;
`;

interface Props {
  onNext: () => void;
}

export const ESPNPrivateLeague: React.FC<Props> = ({ onNext }) => {
  return (
    <Container>
      <IconContainer>
        <Lock size={32} color="#ef4444" />
      </IconContainer>
      
      <Title>Private League Detected</Title>
      
      <Message>
        Looks like your ESPN league is private and we can't get the details, but you can still talk to our hosts and ask for feedback!
      </Message>
      
      <SuggestionBox>
        <SuggestionTitle>
          <MessageCircle size={16} />
          What You Can Do
        </SuggestionTitle>
        <SuggestionText>
          Our AI hosts are experts in fantasy football and can provide general advice, draft strategies, waiver wire tips, and answer any fantasy questions you have!
        </SuggestionText>
      </SuggestionBox>
      
      <NextButton onClick={onNext}>
        <MessageCircle size={20} />
        Talk to Our Hosts
        <ArrowRight size={20} />
      </NextButton>
    </Container>
  );
};
